import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Package, Plus, Save, Trash2, Pencil, ListChecks, AlertCircle, CalendarClock, Eye,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { Modal } from '../components/Modal';
import { useAuth } from '../hooks/useAuth';
import { getDateLocale } from '../i18n';
import { ADDITIONAL_SERVICE_KIND, ADDITIONAL_SERVICE_PRICING_MODEL } from '@harbormaster/shared';

interface MarinaServiceRow {
  id: number;
  name: string;
  description: string | null;
  confirmation_email_note?: string | null;
  service_kind: string;
  is_paid_service: boolean;
  price_amount: number | null;
  pricing_model?: string;
  minimum_units?: number | null;
  currency: string;
  management_view_enabled: boolean;
  allow_ai_booking: boolean;
  require_berth_booking: boolean;
  collect_guest_name: boolean;
  collect_guest_email: boolean;
  collect_guest_phone: boolean;
  is_active: boolean;
  sort_order: number;
  advance_booking_required?: boolean;
  advance_booking_min_hours?: number;
}

interface ServiceBookingRow {
  id: number;
  service_id: number;
  slot_id: number | null;
  reservation_code?: string | null;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  linked_booking_id: string | null;
  status: string;
  notes: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  payment_status?: string;
  amount_cents?: number | null;
  booking_source?: string;
  stripe_payment_intent_id?: string | null;
  created_at: string;
  updated_at?: string;
}

const KIND_OPTIONS = [
  { value: ADDITIONAL_SERVICE_KIND.SAUNA, labelKey: 'additionalServices.kindSauna' as const },
  { value: ADDITIONAL_SERVICE_KIND.BIKE_RENTAL, labelKey: 'additionalServices.kindBike' as const },
  { value: ADDITIONAL_SERVICE_KIND.LAUNDRY, labelKey: 'additionalServices.kindLaundry' as const },
  { value: ADDITIONAL_SERVICE_KIND.CUSTOM, labelKey: 'additionalServices.kindCustom' as const },
];

const STATUS_OPTIONS = ['pending', 'confirmed', 'completed', 'cancelled'] as const;

const STATUS_I18N = {
  pending: 'additionalServices.statusPending',
  confirmed: 'additionalServices.statusConfirmed',
  completed: 'additionalServices.statusCompleted',
  cancelled: 'additionalServices.statusCancelled',
} as const;

const PRICING_OPTIONS = [
  { value: ADDITIONAL_SERVICE_PRICING_MODEL.FLAT, labelKey: 'additionalServices.pricingModelFlat' as const },
  { value: ADDITIONAL_SERVICE_PRICING_MODEL.PER_HOUR, labelKey: 'additionalServices.pricingModelPerHour' as const },
  { value: ADDITIONAL_SERVICE_PRICING_MODEL.PER_DAY, labelKey: 'additionalServices.pricingModelPerDay' as const },
];

function paidServicePriceLabel(
  svc: MarinaServiceRow,
  t: (key: string, opts?: Record<string, string | number>) => string,
): string {
  if (svc.price_amount == null) return t('additionalServices.paidService');
  const p = Number(svc.price_amount).toFixed(2);
  const c = svc.currency;
  const pm = svc.pricing_model ?? ADDITIONAL_SERVICE_PRICING_MODEL.FLAT;
  const min = svc.minimum_units != null && svc.minimum_units > 0 ? svc.minimum_units : null;
  if (pm === ADDITIONAL_SERVICE_PRICING_MODEL.PER_HOUR) {
    return min != null
      ? t('additionalServices.priceLinePerHourMin', { amount: p, currency: c, min })
      : t('additionalServices.priceLinePerHour', { amount: p, currency: c });
  }
  if (pm === ADDITIONAL_SERVICE_PRICING_MODEL.PER_DAY) {
    return min != null
      ? t('additionalServices.priceLinePerDayMin', { amount: p, currency: c, min })
      : t('additionalServices.priceLinePerDay', { amount: p, currency: c });
  }
  return t('additionalServices.priceLineFlat', { amount: p, currency: c });
}

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(s: string): string | null {
  if (!s.trim()) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function formatExtraBookingSchedule(
  locale: string,
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start) return '—';
  const a = new Date(start);
  if (Number.isNaN(a.getTime())) return '—';
  const longFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  const timeOnly = new Intl.DateTimeFormat(locale, { timeStyle: 'short' });
  const s1 = longFmt.format(a);
  if (!end) return s1;
  const b = new Date(end);
  if (Number.isNaN(b.getTime())) return s1;
  const sameCalDay =
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const s2 = sameCalDay ? timeOnly.format(b) : longFmt.format(b);
  return `${s1} – ${s2}`;
}

export function AdditionalServicesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { canWrite, activeMarinaId } = useAuth();
  const [services, setServices] = useState<MarinaServiceRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [serviceModal, setServiceModal] = useState<'add' | MarinaServiceRow | null>(null);
  const [deleteService, setDeleteService] = useState<MarinaServiceRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [serviceDetailTab, setServiceDetailTab] = useState<'entries' | 'slots'>('entries');

  const [bookings, setBookings] = useState<ServiceBookingRow[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [entryModal, setEntryModal] = useState<'add' | ServiceBookingRow | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<ServiceBookingRow | null>(null);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAdditionalServices();
      setServices((data.services ?? []) as unknown as MarinaServiceRow[]);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarinaId]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const loadBookings = useCallback(async (svc: MarinaServiceRow) => {
    setBookingsLoading(true);
    try {
      const data = await api.getAdditionalServiceBookings(svc.id);
      setBookings((data.bookings ?? []) as unknown as ServiceBookingRow[]);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  const resolvedSelectedServiceId = useMemo(() => {
    if (services.length === 0) return null;
    if (selectedServiceId != null && services.some((s) => s.id === selectedServiceId)) return selectedServiceId;
    return services[0]?.id ?? null;
  }, [services, selectedServiceId]);

  const selectedService = useMemo(
    () =>
      resolvedSelectedServiceId != null
        ? services.find((s) => s.id === resolvedSelectedServiceId)
        : undefined,
    [services, resolvedSelectedServiceId],
  );

  useEffect(() => {
    setServiceDetailTab('entries');
  }, [resolvedSelectedServiceId]);

  useEffect(() => {
    if (selectedService?.management_view_enabled) {
      void loadBookings(selectedService);
    } else {
      setBookings([]);
      setBookingsLoading(false);
    }
  }, [selectedService, loadBookings]);

  const handleDeleteService = async () => {
    if (!deleteService || deleting) return;
    setDeleting(true);
    try {
      await api.deleteAdditionalService(deleteService.id);
      toast.success(t('additionalServices.serviceDeleted'));
      setDeleteService(null);
      await fetchServices();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!deleteEntry) return;
    try {
      await api.deleteAdditionalServiceBooking(deleteEntry.id);
      toast.success(t('additionalServices.entryDeleted'));
      setDeleteEntry(null);
      if (selectedService) void loadBookings(selectedService);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-brand-600" />
            {t('additionalServices.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{t('additionalServices.subtitle')}</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setServiceModal('add')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> {t('additionalServices.addService')}
          </button>
        )}
      </div>

      {!canWrite && (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <p>{t('additionalServices.staffReadOnly')}</p>
        </div>
      )}

      {services.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-16 text-center text-sm text-gray-400">
          {t('additionalServices.noServices')}
        </div>
      ) : selectedService ? (
        <div className="space-y-0">
          <div className="flex gap-1 overflow-x-auto pb-0 mb-[-1px] z-10">
            {services.map((svc) => (
              <button
                key={svc.id}
                type="button"
                onClick={() => setSelectedServiceId(svc.id)}
                className={`shrink-0 px-4 py-2.5 rounded-t-lg text-sm font-medium border transition-colors border-b-0 max-w-[12rem] sm:max-w-xs ${
                  resolvedSelectedServiceId === svc.id
                    ? 'bg-white border-gray-200 text-brand-700 shadow-sm relative z-[1]'
                    : 'bg-gray-50 border-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                } ${!svc.is_active ? 'opacity-70' : ''}`}
              >
                <span className="block truncate text-left">{svc.name}</span>
              </button>
            ))}
          </div>

          <div className="bg-white rounded-b-xl rounded-tr-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-4 border-b border-gray-100 bg-gray-50/60 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">{selectedService.name}</h2>
                  {!selectedService.is_active && (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                      {t('additionalServices.inactive')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {KIND_OPTIONS.find((k) => k.value === selectedService.service_kind)?.labelKey
                    ? t(KIND_OPTIONS.find((k) => k.value === selectedService.service_kind)!.labelKey)
                    : selectedService.service_kind}
                </p>
                {selectedService.description ? (
                  <p className="text-sm text-gray-600 leading-relaxed">{selectedService.description}</p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {selectedService.is_paid_service ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-800">
                      {paidServicePriceLabel(selectedService, t)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {t('additionalServices.freeService')}
                    </span>
                  )}
                  {selectedService.management_view_enabled ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800">
                      <ListChecks className="w-3 h-3" />
                      {t('additionalServices.managementView')}
                    </span>
                  ) : null}
                  {selectedService.allow_ai_booking ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-800">
                      {t('additionalServices.allowAiBookingShort')}
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-gray-500 pt-1">
                  <span className="font-medium text-gray-600">{t('additionalServices.collectFields')}:</span>{' '}
                  {[
                    selectedService.collect_guest_name && t('additionalServices.collectName'),
                    selectedService.collect_guest_email && t('additionalServices.collectEmail'),
                    selectedService.collect_guest_phone && t('additionalServices.collectPhone'),
                  ]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </div>
              </div>
              {canWrite ? (
                <div className="flex items-center gap-1 shrink-0 lg:self-start">
                  <button
                    type="button"
                    onClick={() => setServiceModal(selectedService)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 bg-white hover:border-brand-200 hover:text-brand-700"
                    aria-label={t('common.edit')}
                  >
                    <Pencil className="w-4 h-4" />
                    {t('common.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteService(selectedService)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-700 border border-red-100 bg-red-50/80 hover:bg-red-50"
                    aria-label={t('common.delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('common.delete')}
                  </button>
                </div>
              ) : null}
            </div>

            {selectedService.management_view_enabled ? (
              <div className="p-4 lg:p-5">
                {canWrite ? (
                  <div className="flex flex-wrap gap-2 mb-5 p-1 bg-gray-100 rounded-lg w-full sm:w-fit">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={serviceDetailTab === 'entries'}
                      onClick={() => setServiceDetailTab('entries')}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        serviceDetailTab === 'entries'
                          ? 'bg-white text-brand-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {t('additionalServices.tabEntries')}
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={serviceDetailTab === 'slots'}
                      onClick={() => setServiceDetailTab('slots')}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        serviceDetailTab === 'slots'
                          ? 'bg-white text-brand-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <CalendarClock className="w-3.5 h-3.5" />
                      {t('additionalServices.timeSlots')}
                    </button>
                  </div>
                ) : null}

                {serviceDetailTab === 'entries' || !canWrite ? (
                  <BookingsPanel
                    service={selectedService}
                    bookings={bookings}
                    loading={bookingsLoading}
                    inputClass={inputClass}
                    onRefresh={() => loadBookings(selectedService)}
                    onAddEntry={() => setEntryModal('add')}
                    onEditEntry={(row) => setEntryModal(row)}
                    onDeleteEntry={(row) => setDeleteEntry(row)}
                  />
                ) : (
                  <SlotsPanel service={selectedService} inputClass={inputClass} />
                )}
              </div>
            ) : (
              <div className="px-4 py-10 text-center text-sm text-gray-500 border-t border-gray-50">
                {t('additionalServices.managementViewHint')}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {serviceModal && (
        <ServiceFormModal
          mode={serviceModal}
          inputClass={inputClass}
          onClose={() => setServiceModal(null)}
          onSaved={async (created) => {
            setServiceModal(null);
            await fetchServices();
            if (created?.id != null) setSelectedServiceId(created.id);
          }}
        />
      )}

      <Modal
        open={!!deleteService}
        onClose={() => setDeleteService(null)}
        title={t('additionalServices.deleteServiceTitle')}
      >
        <p className="text-sm text-gray-600">
          {t('additionalServices.deleteServiceConfirm', { name: deleteService?.name ?? '' })}
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={() => setDeleteService(null)}
            className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={deleting}
            onClick={handleDeleteService}
            className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {deleting ? t('common.deleting') : t('common.delete')}
          </button>
        </div>
      </Modal>

      {selectedService && entryModal ? (
        <EntryFormModal
          service={selectedService}
          mode={entryModal}
          inputClass={inputClass}
          onClose={() => setEntryModal(null)}
          onSaved={() => {
            setEntryModal(null);
            void loadBookings(selectedService);
          }}
        />
      ) : null}

      <Modal
        open={!!deleteEntry}
        onClose={() => setDeleteEntry(null)}
        title={t('additionalServices.deleteEntryTitle')}
      >
        <p className="text-sm text-gray-600">{t('additionalServices.deleteEntryConfirm')}</p>
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={() => setDeleteEntry(null)}
            className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleDeleteEntry}
            className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            {t('common.delete')}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function ServiceFormModal({
  mode,
  inputClass,
  onClose,
  onSaved,
}: {
  mode: 'add' | MarinaServiceRow;
  inputClass: string;
  onClose: () => void;
  onSaved: (created?: { id: number }) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = mode !== 'add';
  const initial = isEdit ? mode : null;

  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [confirmationEmailNote, setConfirmationEmailNote] = useState(initial?.confirmation_email_note ?? '');
  const [serviceKind, setServiceKind] = useState(initial?.service_kind ?? ADDITIONAL_SERVICE_KIND.CUSTOM);
  const [isPaid, setIsPaid] = useState(initial?.is_paid_service ?? false);
  const [price, setPrice] = useState(
    initial?.price_amount != null ? String(initial.price_amount) : '',
  );
  const [pricingModel, setPricingModel] = useState(
    initial?.pricing_model ?? ADDITIONAL_SERVICE_PRICING_MODEL.FLAT,
  );
  const [minimumUnits, setMinimumUnits] = useState(
    initial?.minimum_units != null && initial.minimum_units > 0 ? String(initial.minimum_units) : '',
  );
  const [currency, setCurrency] = useState(initial?.currency ?? 'EUR');
  const [managementView, setManagementView] = useState(initial?.management_view_enabled ?? false);
  const [allowAiBooking, setAllowAiBooking] = useState(initial?.allow_ai_booking ?? false);
  const [requireBerthBooking, setRequireBerthBooking] = useState(initial?.require_berth_booking ?? true);
  const [advanceBookingRequired, setAdvanceBookingRequired] = useState(initial?.advance_booking_required ?? false);
  const [advanceHours, setAdvanceHours] = useState(() => {
    const h = initial?.advance_booking_min_hours;
    if (h != null && h >= 1 && h <= 168) return String(h);
    return '2';
  });
  const [collectName, setCollectName] = useState(initial?.collect_guest_name ?? true);
  const [collectEmail, setCollectEmail] = useState(initial?.collect_guest_email ?? true);
  const [collectPhone, setCollectPhone] = useState(initial?.collect_guest_phone ?? false);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t('additionalServices.serviceNameRequired'));
      return;
    }
    if (advanceBookingRequired) {
      const n = parseInt(advanceHours, 10);
      if (!Number.isFinite(n) || n < 1 || n > 168) {
        toast.error(t('additionalServices.advanceBookingHoursInvalid'));
        return;
      }
    }
    setSaving(true);
    const body: Record<string, unknown> = {
      name: name.trim(),
      description: description.trim() || null,
      confirmation_email_note: confirmationEmailNote.trim() || null,
      service_kind: serviceKind,
      is_paid_service: isPaid,
      currency: currency.trim().toUpperCase() || 'EUR',
      management_view_enabled: managementView,
      allow_ai_booking: allowAiBooking,
      require_berth_booking: requireBerthBooking,
      advance_booking_required: advanceBookingRequired,
      advance_booking_min_hours: advanceBookingRequired ? Math.min(168, Math.max(1, parseInt(advanceHours, 10) || 2)) : 2,
      collect_guest_name: collectName,
      collect_guest_email: collectEmail,
      collect_guest_phone: collectPhone,
      is_active: isActive,
    };
    if (isPaid && price.trim()) {
      const n = parseFloat(price.replace(',', '.'));
      if (!Number.isNaN(n)) body.price_amount = n;
    } else if (isPaid) {
      body.price_amount = null;
    }
    body.pricing_model = isPaid ? pricingModel : ADDITIONAL_SERVICE_PRICING_MODEL.FLAT;
    if (
      isPaid &&
      (pricingModel === ADDITIONAL_SERVICE_PRICING_MODEL.PER_HOUR ||
        pricingModel === ADDITIONAL_SERVICE_PRICING_MODEL.PER_DAY) &&
      minimumUnits.trim()
    ) {
      const m = parseFloat(minimumUnits.replace(',', '.'));
      body.minimum_units = !Number.isNaN(m) && m > 0 ? m : null;
    } else {
      body.minimum_units = null;
    }
    try {
      if (isEdit) {
        await api.updateAdditionalService(initial!.id, body);
        toast.success(t('additionalServices.serviceUpdated'));
        await onSaved();
      } else {
        const created = (await api.createAdditionalService(body)) as { id: number };
        toast.success(t('additionalServices.serviceCreated'));
        await onSaved({ id: created.id });
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('additionalServices.editServiceTitle') : t('additionalServices.addServiceTitle')}
      maxWidth="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.serviceName')} *</label>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('additionalServices.serviceNamePlaceholder')}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.description')}</label>
          <textarea
            className={inputClass}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('additionalServices.descriptionPlaceholder')}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.confirmationEmailNote')}</label>
          <textarea
            className={inputClass}
            rows={4}
            value={confirmationEmailNote}
            onChange={(e) => setConfirmationEmailNote(e.target.value)}
            placeholder={t('additionalServices.confirmationEmailNotePlaceholder')}
          />
          <p className="text-[11px] text-gray-400 mt-1">{t('additionalServices.confirmationEmailNoteHint')}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.serviceKind')}</label>
          <select
            className={inputClass}
            value={serviceKind}
            onChange={(e) => setServiceKind(e.target.value)}
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k.value} value={k.value}>{t(k.labelKey)}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isPaid}
            onChange={(e) => setIsPaid(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-brand-600"
          />
          <span className="text-sm text-gray-800">{t('additionalServices.paidService')}</span>
        </label>
        {isPaid && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.pricingModel')}</label>
              <select
                className={inputClass}
                value={pricingModel}
                onChange={(e) => setPricingModel(e.target.value)}
              >
                {PRICING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {pricingModel === ADDITIONAL_SERVICE_PRICING_MODEL.FLAT
                    ? t('additionalServices.price')
                    : pricingModel === ADDITIONAL_SERVICE_PRICING_MODEL.PER_HOUR
                      ? t('additionalServices.unitPriceHour')
                      : t('additionalServices.unitPriceDay')}
                </label>
                <input
                  className={inputClass}
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                />
                <p className="text-[11px] text-gray-400 mt-1">{t('additionalServices.priceHint')}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.currency')}</label>
                <input
                  className={inputClass}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  maxLength={3}
                />
              </div>
            </div>
            {(pricingModel === ADDITIONAL_SERVICE_PRICING_MODEL.PER_HOUR ||
              pricingModel === ADDITIONAL_SERVICE_PRICING_MODEL.PER_DAY) && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.minimumUnits')}</label>
                <input
                  className={inputClass}
                  value={minimumUnits}
                  onChange={(e) => setMinimumUnits(e.target.value)}
                  placeholder={pricingModel === ADDITIONAL_SERVICE_PRICING_MODEL.PER_HOUR ? '2' : '1'}
                />
                <p className="text-[11px] text-gray-400 mt-1">{t('additionalServices.minimumUnitsHint')}</p>
              </div>
            )}
          </div>
        )}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={managementView}
            onChange={(e) => setManagementView(e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-brand-600"
          />
          <span>
            <span className="text-sm text-gray-800 block">{t('additionalServices.managementView')}</span>
            <span className="text-xs text-gray-500">{t('additionalServices.managementViewHint')}</span>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allowAiBooking}
            onChange={(e) => setAllowAiBooking(e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-brand-600"
          />
          <span>
            <span className="text-sm text-gray-800 block">{t('additionalServices.allowAiBooking')}</span>
            <span className="text-xs text-gray-500">{t('additionalServices.allowAiBookingHint')}</span>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={requireBerthBooking}
            onChange={(e) => setRequireBerthBooking(e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-brand-600"
          />
          <span>
            <span className="text-sm text-gray-800 block">{t('additionalServices.requireBerthBooking')}</span>
            <span className="text-xs text-gray-500">{t('additionalServices.requireBerthBookingHint')}</span>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={advanceBookingRequired}
            onChange={(e) => setAdvanceBookingRequired(e.target.checked)}
            className="w-4 h-4 mt-0.5 rounded border-gray-300 text-brand-600"
          />
          <span>
            <span className="text-sm text-gray-800 block">{t('additionalServices.advanceBookingRequired')}</span>
            <span className="text-xs text-gray-500">{t('additionalServices.advanceBookingRequiredHint')}</span>
          </span>
        </label>
        {advanceBookingRequired && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.advanceBookingMinHours')}</label>
            <input
              type="number"
              min={1}
              max={168}
              step={1}
              className={inputClass}
              value={advanceHours}
              onChange={(e) => setAdvanceHours(e.target.value)}
            />
            <p className="text-[11px] text-gray-400 mt-1">{t('additionalServices.advanceBookingMinHoursHint')}</p>
          </div>
        )}
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">{t('additionalServices.collectFields')}</p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={collectName} onChange={(e) => setCollectName(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-brand-600" />
              {t('additionalServices.collectName')}
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={collectEmail} onChange={(e) => setCollectEmail(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-brand-600" />
              {t('additionalServices.collectEmail')}
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={collectPhone} onChange={(e) => setCollectPhone(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-brand-600" />
              {t('additionalServices.collectPhone')}
            </label>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-brand-600"
          />
          <span className="text-sm text-gray-800">{t('additionalServices.active')}</span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function BookingsPanel({
  service,
  bookings,
  loading,
  inputClass,
  onRefresh,
  onAddEntry,
  onEditEntry,
  onDeleteEntry,
}: {
  service: MarinaServiceRow;
  bookings: ServiceBookingRow[];
  loading: boolean;
  inputClass: string;
  onRefresh: () => void;
  onAddEntry: () => void;
  onEditEntry: (row: ServiceBookingRow) => void;
  onDeleteEntry: (row: ServiceBookingRow) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const locale = getDateLocale();
  const [detailRow, setDetailRow] = useState<ServiceBookingRow | null>(null);

  const rowRef = (row: ServiceBookingRow) =>
    typeof row.reservation_code === 'string' && row.reservation_code.trim()
      ? row.reservation_code.trim()
      : `#${row.id}`;

  return (
    <section className="space-y-4" aria-labelledby={`bookings-heading-${service.id}`}>
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start sm:justify-between">
        <h3 id={`bookings-heading-${service.id}`} className="text-base font-semibold text-gray-900">
          {t('additionalServices.bookingsPanelHeading', { name: service.name })}
        </h3>
        <button
          type="button"
          onClick={onAddEntry}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 shrink-0 self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" />
          {t('additionalServices.addEntry')}
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
        </div>
      ) : bookings.length === 0 ? (
        <p className="text-sm text-gray-500 py-10 text-center border border-dashed border-gray-200 rounded-xl">{t('additionalServices.noBookings')}</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 -mx-1">
          <table className="min-w-[900px] w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                <th className="pb-2 pr-3 whitespace-nowrap">{t('additionalServices.colReservationCode')}</th>
                <th className="pb-2 pr-3 min-w-[200px]">{t('additionalServices.colSchedule')}</th>
                <th className="pb-2 pr-3 min-w-[180px]">{t('additionalServices.colGuest')}</th>
                <th className="pb-2 pr-3">{t('additionalServices.status')}</th>
                <th className="pb-2 pr-3">{t('additionalServices.payment')}</th>
                <th className="pb-2 pr-3">{t('additionalServices.source')}</th>
                <th className="pb-2">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((row) => (
                <tr key={row.id} className="border-b border-gray-100 align-top">
                  <td className="py-2 pr-3 font-mono text-xs font-semibold whitespace-nowrap text-brand-800">
                    {rowRef(row)}
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-700 leading-snug">
                    {formatExtraBookingSchedule(locale, row.start_datetime, row.end_datetime)}
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    <div className="font-medium text-gray-900">{row.guest_name ?? '—'}</div>
                    {row.guest_email && (
                      <div className="text-gray-500 break-all mt-0.5">{row.guest_email}</div>
                    )}
                    {row.guest_phone && <div className="text-gray-500 mt-0.5">{row.guest_phone}</div>}
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      className={inputClass + ' py-1 text-xs'}
                      value={row.status}
                      onChange={async (e) => {
                        try {
                          await api.updateAdditionalServiceBooking(row.id, { status: e.target.value });
                          onRefresh();
                        } catch (err) {
                          toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
                        }
                      }}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{t(STATUS_I18N[s])}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-600">
                    <div>{row.payment_status ?? '—'}</div>
                    {row.amount_cents != null && (
                      <div className="text-gray-500">
                        {(row.amount_cents / 100).toFixed(2)} {service.currency}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-600 whitespace-nowrap">
                    {(row.booking_source ?? '').replace(/_/g, ' ') || '—'}
                  </td>
                  <td className="py-2">
                    <div className="flex gap-0.5">
                      <button
                        type="button"
                        onClick={() => setDetailRow(row)}
                        className="p-1.5 text-brand-700 hover:bg-brand-50 rounded"
                        title={t('additionalServices.viewBookingDetail')}
                        aria-label={t('additionalServices.viewBookingDetail')}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => onEditEntry(row)} className="p-1.5 text-brand-600 hover:bg-brand-50 rounded">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => onDeleteEntry(row)} className="p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detailRow && (
        <Modal
          open
          maxWidth="max-w-lg"
          onClose={() => setDetailRow(null)}
          title={t('additionalServices.bookingDetailTitle')}
        >
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('additionalServices.colReservationCode')}</dt>
              <dd className="font-mono font-semibold text-brand-900 mt-0.5">{rowRef(detailRow)}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('additionalServices.colSchedule')}</dt>
              <dd className="text-gray-900 mt-0.5 leading-relaxed">{formatExtraBookingSchedule(locale, detailRow.start_datetime, detailRow.end_datetime)}</dd>
              {detailRow.start_datetime && (
                <dd className="text-xs text-gray-400 mt-1 font-mono break-all">{detailRow.start_datetime}</dd>
              )}
              {detailRow.end_datetime && (
                <dd className="text-xs text-gray-400 mt-0.5 font-mono break-all">{detailRow.end_datetime}</dd>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.guestName')}</dt>
                <dd className="mt-0.5">{detailRow.guest_name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.guestEmail')}</dt>
                <dd className="mt-0.5 break-all">{detailRow.guest_email ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.guestPhone')}</dt>
                <dd className="mt-0.5">{detailRow.guest_phone ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.slotIdColumn')}</dt>
                <dd className="mt-0.5">{detailRow.slot_id != null ? String(detailRow.slot_id) : '—'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.linkedBookingId')}</dt>
                <dd className="mt-0.5 font-mono text-xs break-all">{detailRow.linked_booking_id ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.status')}</dt>
                <dd className="mt-0.5 capitalize">{detailRow.status.replace(/_/g, ' ')}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.payment')}</dt>
                <dd className="mt-0.5">
                  {(detailRow.payment_status ?? '—').replace(/_/g, ' ')}
                  {detailRow.amount_cents != null && (
                    <span className="block text-gray-600">
                      {(detailRow.amount_cents / 100).toFixed(2)} {service.currency}
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.source')}</dt>
                <dd className="mt-0.5">{(detailRow.booking_source ?? '—').replace(/_/g, ' ')}</dd>
              </div>
              {detailRow.stripe_payment_intent_id ? (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium text-gray-500">{t('additionalServices.stripePaymentIntent')}</dt>
                  <dd className="mt-0.5 font-mono text-xs break-all">{detailRow.stripe_payment_intent_id}</dd>
                </div>
              ) : null}
              <div>
                <dt className="text-xs font-medium text-gray-500">{t('additionalServices.createdAt')}</dt>
                <dd className="mt-0.5 text-xs">{new Date(detailRow.created_at).toLocaleString(locale)}</dd>
              </div>
              {detailRow.updated_at ? (
                <div>
                  <dt className="text-xs font-medium text-gray-500">{t('additionalServices.updatedAt')}</dt>
                  <dd className="mt-0.5 text-xs">{new Date(detailRow.updated_at).toLocaleString(locale)}</dd>
                </div>
              ) : null}
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">{t('common.notes')}</dt>
              <dd className="mt-1 text-gray-800 whitespace-pre-wrap rounded-lg bg-gray-50 border border-gray-100 p-3 min-h-[56px]">
                {detailRow.notes?.trim() ? detailRow.notes : '—'}
              </dd>
            </div>
          </dl>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => {
                const r = detailRow;
                setDetailRow(null);
                onEditEntry(r);
              }}
              className="px-3 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700"
            >
              {t('common.edit')}
            </button>
            <button type="button" onClick={() => setDetailRow(null)} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              {t('common.close')}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function EntryFormModal({
  service,
  mode,
  inputClass,
  onClose,
  onSaved,
}: {
  service: MarinaServiceRow;
  mode: 'add' | ServiceBookingRow;
  inputClass: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const isEdit = mode !== 'add';
  const initial = isEdit ? mode : null;

  const [guestName, setGuestName] = useState(initial?.guest_name ?? '');
  const [guestEmail, setGuestEmail] = useState(initial?.guest_email ?? '');
  const [guestPhone, setGuestPhone] = useState(initial?.guest_phone ?? '');
  const [linkedId, setLinkedId] = useState(initial?.linked_booking_id ?? '');
  const [status, setStatus] = useState(initial?.status ?? 'pending');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [startLocal, setStartLocal] = useState(toDatetimeLocalValue(initial?.start_datetime));
  const [endLocal, setEndLocal] = useState(toDatetimeLocalValue(initial?.end_datetime));
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const n = guestName.trim();
    const e = guestEmail.trim();
    const p = guestPhone.trim();
    if (service.collect_guest_name && !n) return false;
    if (service.collect_guest_email && !e) return false;
    if (service.collect_guest_phone && !p) return false;
    if (!n && !e && !p) return false;
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(t('additionalServices.fieldRequired'));
      return;
    }
    setSaving(true);
    const body: Record<string, unknown> = {
      guest_name: guestName.trim() || null,
      guest_email: guestEmail.trim() || null,
      guest_phone: guestPhone.trim() || null,
      linked_booking_id: linkedId.trim() || null,
      status,
      notes: notes.trim() || null,
      start_datetime: fromDatetimeLocalValue(startLocal),
      end_datetime: fromDatetimeLocalValue(endLocal),
    };
    try {
      if (isEdit) {
        await api.updateAdditionalServiceBooking(initial!.id, body);
        toast.success(t('additionalServices.entryUpdated'));
      } else {
        await api.createAdditionalServiceBooking({ ...body, service_id: service.id });
        toast.success(t('additionalServices.entryCreated'));
      }
        await onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('additionalServices.editEntryTitle') : t('additionalServices.addEntryTitle')}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {service.collect_guest_name && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.guestName')} *</label>
            <input className={inputClass} value={guestName} onChange={(e) => setGuestName(e.target.value)} />
          </div>
        )}
        {service.collect_guest_email && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.guestEmail')} *</label>
            <input className={inputClass} type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} />
          </div>
        )}
        {service.collect_guest_phone && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.guestPhone')} *</label>
            <input className={inputClass} value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} />
          </div>
        )}
        {!service.collect_guest_name && !service.collect_guest_email && !service.collect_guest_phone && (
          <>
            <p className="text-xs text-gray-600 mb-2">{t('additionalServices.entryContactHint')}</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.guestName')}</label>
              <input className={inputClass} value={guestName} onChange={(e) => setGuestName(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.guestEmail')}</label>
              <input className={inputClass} type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.guestPhone')}</label>
              <input className={inputClass} value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} />
            </div>
          </>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.linkedBookingId')}</label>
          <input className={`${inputClass} font-mono text-xs`} value={linkedId} onChange={(e) => setLinkedId(e.target.value)} />
          <p className="text-[11px] text-gray-400 mt-1">{t('additionalServices.linkedBookingHint')}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.status')}</label>
          <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{t(STATUS_I18N[s])}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.startTime')}</label>
            <input
              type="datetime-local"
              className={inputClass}
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.endTime')}</label>
            <input
              type="datetime-local"
              className={inputClass}
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.notes')}</label>
          <textarea className={inputClass} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface SlotRow {
  id: number;
  starts_at: string;
  ends_at: string;
  capacity: number;
  label: string | null;
  is_active: boolean;
}

function SlotsPanel({
  service,
  inputClass,
}: {
  service: MarinaServiceRow;
  inputClass: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { error: toastError } = toast;
  const locale = getDateLocale();
  const [slots, setSlots] = useState<SlotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [startsLocal, setStartsLocal] = useState('');
  const [endsLocal, setEndsLocal] = useState('');
  const [capacity, setCapacity] = useState('1');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAdditionalServiceSlots(service.id);
      setSlots((data.slots ?? []) as unknown as SlotRow[]);
    } catch (err) {
      if (err instanceof ApiError) toastError(err.message);
    } finally {
      setLoading(false);
    }
  }, [service.id, toastError]);

  useEffect(() => { void load(); }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const starts = fromDatetimeLocalValue(startsLocal);
    const ends = fromDatetimeLocalValue(endsLocal);
    if (!starts || !ends) {
      toast.error(t('additionalServices.slotTimesRequired'));
      return;
    }
    if (new Date(ends) <= new Date(starts)) {
      toast.error(t('additionalServices.slotEndAfterStart'));
      return;
    }
    const cap = parseInt(capacity, 10);
    if (Number.isNaN(cap) || cap < 1) {
      toast.error(t('additionalServices.slotCapacityInvalid'));
      return;
    }
    setSaving(true);
    try {
      await api.createAdditionalServiceSlot(service.id, {
        starts_at: starts,
        ends_at: ends,
        capacity: cap,
        label: label.trim() || null,
      });
      toast.success(t('additionalServices.slotCreated'));
      setStartsLocal('');
      setEndsLocal('');
      setCapacity('1');
      setLabel('');
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.deleteAdditionalServiceSlot(id);
      toast.success(t('additionalServices.slotDeleted'));
      void load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    }
  };

  return (
    <section className="space-y-5" aria-labelledby={`slots-heading-${service.id}`}>
      <h3 id={`slots-heading-${service.id}`} className="text-base font-semibold text-gray-900">
        {t('additionalServices.slotsPanelHeading', { name: service.name })}
      </h3>
      <form onSubmit={handleAdd} className="space-y-3 mb-2 pb-6 border-b border-gray-100">
        <p className="text-xs text-gray-600">{t('additionalServices.slotsIntro')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.slotStarts')}</label>
            <input type="datetime-local" className={inputClass} value={startsLocal} onChange={(e) => setStartsLocal(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.slotEnds')}</label>
            <input type="datetime-local" className={inputClass} value={endsLocal} onChange={(e) => setEndsLocal(e.target.value)} required />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.slotCapacity')}</label>
            <input type="number" min={1} className={inputClass} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('additionalServices.slotLabel')}</label>
            <input type="text" className={inputClass} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('additionalServices.slotLabelPlaceholder')} />
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          {saving ? t('common.saving') : t('additionalServices.addSlot')}
        </button>
      </form>
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
        </div>
      ) : slots.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8 border border-dashed border-gray-200 rounded-xl">{t('additionalServices.noSlots')}</p>
      ) : (
        <ul className="space-y-2 max-h-[min(28rem,calc(100vh-22rem))] overflow-y-auto pr-1">
          {slots.map((sl) => (
            <li
              key={sl.id}
              className="flex items-start justify-between gap-2 text-sm border border-gray-100 rounded-lg px-3 py-2"
            >
              <div>
                <div className="font-medium text-gray-900">
                  {new Date(sl.starts_at).toLocaleString(locale)} → {new Date(sl.ends_at).toLocaleString(locale)}
                </div>
                {sl.label && <div className="text-xs text-gray-500">{sl.label}</div>}
                <div className="text-xs text-gray-600">
                  {t('additionalServices.slotCapacity')}: {sl.capacity}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(sl.id)}
                className="p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded shrink-0"
                aria-label={t('common.delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
